import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import {
  itemsCell,
  listingNote,
  orderRow,
  ORDERS_SCOPE,
  PAUSE_REFUSAL,
  stateOf,
  statesPresent,
  timerCell,
  type Acceptance,
  type AcceptanceState,
} from "../orders.ts";
import { boundedNote } from "../board.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `orders` in `22 WP21 Merchant Portal.dc.html`: accept or reject before the timer runs out.
 *
 * **The one countdown on this platform with a published source.** Every other board that draws one
 * has had to record the absence — the support console's case queue most recently, where nothing
 * publishes a reply deadline at all. Here `acceptanceDeadline` is a real field on a real row,
 * written once by the order-placed ingest, and COM-06's listing orders by it: the design's "Accept
 * or reject each order before the timer runs out" is the service's own ordering.
 *
 * What is drawn from data and what is not — `../orders.ts` argues each:
 *
 * | drawn                           | source                                                    |
 * | ------------------------------- | --------------------------------------------------------- |
 * | the rows                        | COM-06 `GET /v1/merchants/{merchantId}/orders`            |
 * | `Order` — `co_7Q31`             | `orderId`. The design's short code does not exist         |
 * | `Items` — `3 items`             | `itemUnits`                                               |
 * | `Items` — `KES 1,240.00`        | **nothing** — the order total is COM-05's                 |
 * | `Timer` — `2:41 left to accept` | computed from `acceptanceDeadline`, against this clock    |
 * | `preparing`, `ready`            | **nothing** — COM-07's states, and its reads are by id    |
 * | the four tiles                  | no listing publishes a total (R-04); no tile row here     |
 * | the primary command             | drawn and refused — `PAUSE_REFUSAL`                       |
 *
 * **The source chip is the design's own `COM-06 · COM-07` and this calls one.** CLAUDE.md §1 governs
 * the copy; editing it to `COM-06` would be redrawing the design to match the build. COM-07 holds
 * the preparation half — `preparing`, `ready by 12:55` — and every one of its reads is by order
 * identifier, so a board cannot ask it anything. The footnote under the grid says so.
 *
 * **The clock is re-read on a timer, which no other board here does.** A countdown that only moves
 * when a merchant reloads is not a countdown, and this is the one board whose whole value is that
 * the number changes. It ticks once a second and fetches nothing: the rows are held, and only the
 * clock advances.
 */

const acceptance = services.commerce__merchant_order_acceptance;

export interface IncomingOrdersProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Today › Incoming orders`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Incoming orders";
const SOURCE = "COM-06 · COM-07";
const DESCRIPTION =
  "Accept or reject each order before the timer runs out, then move it through preparation.";
const COLUMNS = ["Order", "Items", "Timer"] as const;
const NOTE =
  "An order not accepted in time is rejected automatically and the customer is refunded in full.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.orders[0]`, which is the word on its heading button. */
const PRIMARY = "Pause new orders";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

/** COM-06's published states, so a value outside them is read as the safe one. */
const STATES: readonly AcceptanceState[] = ["pending", "accepted", "rejected", "expired"];

/**
 * Narrow one published row onto the shape this board reads.
 *
 * A state the contract's enum does not cover is read as `expired`, which is the only safe
 * direction: the alternative is a badge with no class, and an order whose state this console cannot
 * read must not be drawn as one still waiting for a decision — a merchant would act on it.
 */
function acceptanceOf(row: {
  tenantId: string;
  orderId: string;
  orderVersion: number;
  merchantId: string;
  locationId: string;
  itemUnits: number;
  acceptanceDeadline: string;
  state: string;
  version: number;
  placedAt: string;
  updatedAt: string;
  decidedBy?: string;
  rejectionReason?: string;
}): Acceptance {
  return {
    tenantId: row.tenantId,
    orderId: row.orderId,
    orderVersion: row.orderVersion,
    merchantId: row.merchantId,
    locationId: row.locationId,
    itemUnits: row.itemUnits,
    acceptanceDeadline: row.acceptanceDeadline,
    state: STATES.find((candidate) => candidate === row.state) ?? "expired",
    version: row.version,
    placedAt: row.placedAt,
    updatedAt: row.updatedAt,
    ...(row.decidedBy === undefined ? {} : { decidedBy: row.decidedBy }),
    ...(row.rejectionReason === undefined ? {} : { rejectionReason: row.rejectionReason }),
  };
}

export function IncomingOrders({ transport, session, crumbs }: IncomingOrdersProps): ReactNode {
  const [rows, setRows] = useState<readonly Acceptance[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  /*
   * The clock, as state, so the countdown moves without a fetch.
   *
   * One second, which is what the design's `2:41` implies. It re-renders the board and nothing
   * else: the rows are held in `rows` and no request is made, so a merchant watching a timer is
   * not hammering the service.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return (): void => {
      clearInterval(tick);
    };
  }, []);

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * The scope asked for before the call, not discovered from a refusal — so a merchant whose role
     * is short of it is told its name rather than shown an empty board. ADR 0049 §3.
     */
    if (!session.scopes.has(ORDERS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [ORDERS_SCOPE] });
      return;
    }

    /*
     * And the merchant, which is the claim this portal is the first channel to need.
     *
     * Said as a forbidden state rather than as an error, because that is what it is: the token does
     * not carry what this board must know. The alternative is worse than an error — a board with no
     * merchant would have to ask for one, and a merchant identifier the browser supplies is a
     * merchant identifier the browser can change. `session.ts` argues it at length.
     */
    const merchantId = session.merchantId;
    if (merchantId === undefined) {
      setFailure({ kind: "forbidden", missingScopes: ["a merchant_id claim on your token"] });
      return;
    }

    /*
     * One call, and no second one for the preparation states.
     *
     * COM-07's reads are `GET /v1/order-preparations/{orderId}`, by identifier — so filling the
     * design's `preparing` and `ready` would mean a request per row, which is the fan-out
     * Document 17 WEB-0743 tolerates for one listing per screen and not for a column.
     */
    try {
      const page = await acceptance
        .createMerchantOrderAcceptanceClient(transport)
        .listMerchantOrders(merchantId, String(PER_FETCH));
      setRows(page.acceptances.map(acceptanceOf));
      setTruncated(typeof page.nextAfter === "string");
    } catch (error) {
      setFailure(stateForError(error, [ORDERS_SCOPE]));
    }
  }, [transport, session.scopes, session.merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((row) => state === "" || stateOf(row).label === state)
          .map((row) => orderRow(row, now));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "orders",
    "ordered by acceptance deadline, soonest first",
  );
  const footnote = listingNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No order is waiting on a decision. A row appears here the moment a customer places one."
      primary={{ label: PRIMARY, refusal: PAUSE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="incoming-orders"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}

/**
 * The two cells an export writes that the grid computes, exported so the sheet and the screen
 * cannot disagree.
 *
 * The `Timer` column is relative — `2:41 left to accept` — and a row read out of a file an hour
 * later has no "now" to be read against, so the export writes the absolute deadline beside it.
 * `../orders.ts` owns both functions.
 */
export { itemsCell, timerCell };
